import type { PreloadedQuery } from 'react-relay';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import type { User } from '@jira/platform__rest-user/src/user.tsx';
// eslint-disable-next-line jira/import-whitelist
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import type { RouteResource } from '@atlassian/react-resource-router';
import workItemViewResourcesQuery, {
	type workItemViewResourcesQuery as WorkItemViewResourcesQueryType,
} from './__generated__/workItemViewResourcesQuery.graphql.ts';
import { getNonNullWorkItemResourcesQueryVariablesForIssueKey } from './workItemResourcesDataFetcher.tsx';

// New 1P attachments resource path: preload the resources query as a Relay route
// resource so SSR and client hydration share the same query reference instead of
// firing the legacy client-side loader after the page has already fetched it.
export const workItemResourcesRouteResource: RouteResource<
	PreloadedQuery<WorkItemViewResourcesQueryType, {}>
> = createRelayResource<WorkItemViewResourcesQueryType>({
	type: 'WORK_ITEM_RESOURCES_ROUTE_RESOURCE',
	getQuery: ({ match }, storeContext) => {
		const variables = getNonNullWorkItemResourcesQueryVariablesForIssueKey(
			getIssueKey(match),
			storeContext?.tenantContext?.cloudId,
		);
		return {
			parameters: workItemViewResourcesQuery,
			variables,
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isBrowserOnly: false,
});

export function getWorkItemResources(): (
	| RouteResource<PreloadedQuery<WorkItemViewResourcesQueryType, {}> | null>
	| RouteResource<User | null>
)[] {
	return [
		// currentUserResource remains tied to the SSR attachments experience, but
		// attachments data itself is supplied either by the old browser-only
		// resource or by the new Relay route resource. Keeping those paths mutually
		// exclusive prevents route preload + client fallback from double fetching.
		currentUserResource,
		workItemResourcesRouteResource,
	];
}
